import { makeAutoObservable, observable, reaction, runInAction } from "mobx";
import type DataGridStore from "stores/DataGridStore/DataGridStore";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

// Single source of truth for which grid columns map directly to panel fields
// and are safe to PATCH back. Anything not in this set is derived, placeholder,
// or editable only through another screen.
export const MASTER_PIECE_LIST_EDITABLE_KEYS = new Set([
  "panelMark",
  "panelType",
  "location",
  "lengthInches",
  "priority",
  "color",
  "finish",
  "pourNumber",
  "productionArea",
  "qcStatus",
  "pstpNotes",
  "fcNotes",
  // Import-template fields surfaced as MPL columns
  "level",
  "linearFt",
  "sqFt",
  "releaseStrength",
  "shippingStrength",
  "mixDesign",
  "pmDraftingNotes",
]);

interface Chip {
  id?: number | string;
  panelId?: number | string;
  phase?: string;
  dayNumber?: number;
  scheduledDate?: string | Date | null;
}

interface MoldInstance {
  id: number | string;
  moldInstanceCode?: string;
}

interface Project {
  id: number | string;
  projectNumber?: string;
  projectName?: string;
}

// Reorder into a friendlier yyyy-MM-dd display string; if absent, return ''.
function toDateStr(value: unknown): string {
  if (!value) return "";
  const d = typeof value === "string" ? value.split("T")[0] : null;
  if (d) return d;
  if (value instanceof Date && !isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return "";
}

// How long the grid waits after a live change before re-rendering. See
// `startLiveRows`.
const LIVE_ROWS_DELAY_MS = 1000;

class MasterPieceListStore {
  rootStore: RootStore;

  // Throttled copy of `entities` that the grid renders from while it is
  // mounted. Every socket event on any of the five MPL channels (panels,
  // production-schedule, chips, molds, projects) swaps a source array, which
  // used to re-derive and re-render the entire grid once PER EVENT. A single
  // drag on the Production Schedule emits dozens of chip patches, so on the
  // plant floor the MPL spent its time re-rendering instead of responding —
  // Josh: "it froze my computer every time" (2026-10-19). Coalescing to one
  // render per second keeps the data live without that cost.
  liveEntities: Array<Record<string, unknown>> = [];
  isLive = false;
  private disposeLiveRows: (() => void) | null = null;

  // Reference-identity cache for the derived entities array. MobX
  // observable-array reads return a new array reference each batch, but
  // the underlying entity arrays themselves only swap references when
  // the store's data actually changes (create/patch/remove). We cache
  // the last computed derived array keyed on the tuple of source-array
  // references; if all five are stable, return the cached result.
  //
  // Without this, the getter re-derives every MobX read — which the body
  // does inside a useMemo for `rows`, `valuesByField`, the summary stat
  // strip, etc. At 15k panels × 5 columns × every keystroke, the cost
  // compounded into the multi-second lag Roberto observed.
  private _entitiesCache: {
    panels: unknown;
    schedules: unknown;
    chips: unknown;
    molds: unknown;
    projects: unknown;
    result: Array<Record<string, unknown>>;
  } | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // `_entitiesCache` is intentionally non-reactive — cast through the
    // expected parameter shape because makeAutoObservable's annotation
    // map only lists inferred observable fields.
    makeAutoObservable(this, {
      rootStore: false,
      _entitiesCache: false,
      liveEntities: observable.ref,
      disposeLiveRows: false,
    } as Parameters<typeof makeAutoObservable>[1]);
  }

  /** What the grid renders: the throttled copy while live, else `entities`. */
  get gridEntities(): Array<Record<string, unknown>> {
    return this.isLive ? this.liveEntities : this.entities;
  }

  // Called by the body on mount / unmount. The first value is applied
  // synchronously (fireImmediately), so the grid never renders empty.
  startLiveRows = () => {
    if (this.disposeLiveRows) return;
    this.disposeLiveRows = reaction(
      () => this.entities,
      (next) =>
        runInAction(() => {
          this.liveEntities = next;
          this.isLive = true;
        }),
      { delay: LIVE_ROWS_DELAY_MS, fireImmediately: true }
    );
  };

  stopLiveRows = () => {
    this.disposeLiveRows?.();
    this.disposeLiveRows = null;
    runInAction(() => {
      this.isLive = false;
      // Drop the reference so a 15k-row snapshot isn't held off-screen.
      this.liveEntities = [];
    });
  };

  get entities(): Array<Record<string, unknown>> {
    const panelsStore = this.rootStore.panelsStore as ServiceStore;
    const productionScheduleStore = this.rootStore
      .productionScheduleStore as ServiceStore;
    const chipsStore = (this.rootStore as unknown as {
      panelScheduleChipsStore?: ServiceStore;
    }).panelScheduleChipsStore;
    const moldInstancesStore = (this.rootStore as unknown as {
      moldInstancesStore?: ServiceStore;
    }).moldInstancesStore;
    const projectsStore = (this.rootStore as unknown as {
      projectsStore?: ServiceStore;
    }).projectsStore;

    if (!panelsStore?.entities?.length) return [];

    // Source-array reference snapshot. If MobX hasn't swapped any of
    // these since the last call, the derived array is still valid.
    const panelsRef = panelsStore.entities;
    const schedulesRef = productionScheduleStore?.entities ?? null;
    const chipsRef = chipsStore?.entities ?? null;
    const moldsRef = moldInstancesStore?.entities ?? null;
    const projectsRef = projectsStore?.entities ?? null;

    const cache = this._entitiesCache;
    if (
      cache &&
      cache.panels === panelsRef &&
      cache.schedules === schedulesRef &&
      cache.chips === chipsRef &&
      cache.molds === moldsRef &&
      cache.projects === projectsRef
    ) {
      return cache.result;
    }

    // Index schedules by panelId (one per panel)
    const scheduleByPanelId = new Map<unknown, Record<string, unknown>>();
    if (productionScheduleStore?.entities?.length) {
      for (const s of productionScheduleStore.entities) {
        if (s.panelId != null) scheduleByPanelId.set(s.panelId, s);
      }
    }

    // Index chips by panelId → phase → earliest/latest chip
    const chipsByPanel = new Map<unknown, Chip[]>();
    if (chipsStore?.entities?.length) {
      for (const c of chipsStore.entities as Chip[]) {
        if (c.panelId == null) continue;
        if (!chipsByPanel.has(c.panelId)) chipsByPanel.set(c.panelId, []);
        chipsByPanel.get(c.panelId)!.push(c);
      }
    }

    // Index mold instances by id
    const moldInstanceById = new Map<unknown, MoldInstance>();
    if (moldInstancesStore?.entities?.length) {
      for (const mi of moldInstancesStore.entities as MoldInstance[]) {
        moldInstanceById.set(mi.id, mi);
      }
    }

    // Index projects by id
    const projectById = new Map<unknown, Project>();
    if (projectsStore?.entities?.length) {
      for (const p of projectsStore.entities as Project[]) {
        projectById.set(p.id, p);
      }
    }

    const result = panelsStore.entities.slice().map((panel) => {
      const schedule = scheduleByPanelId.get(panel.panelId ?? panel.id);
      const panelChips = chipsByPanel.get(panel.panelId ?? panel.id) || [];

      // Pick the relevant chip per phase
      const byPhase = (phase: string, pick: "first" | "last") => {
        const list = panelChips
          .filter((c) => c.phase === phase)
          .sort((a, b) => toDateStr(a.scheduledDate).localeCompare(toDateStr(b.scheduledDate)));
        if (list.length === 0) return "";
        return toDateStr(pick === "first" ? list[0].scheduledDate : list[list.length - 1].scheduledDate);
      };

      const volumeCuft = (panel.volumeCuft as number | null | undefined) ?? null;
      const sqFt = (panel.sqFt as number | null | undefined) ?? null;
      const project = projectById.get(panel.projectId);
      const moldInstance = moldInstanceById.get(panel.moldInstanceId);

      const moldCompletionDate = byPhase("moldProduction", "last");
      const setupDate = byPhase("setup", "first");
      const castDate = byPhase("casting", "first");
      const shippingChipDate = byPhase("shipping", "first");
      const finishingLastDate = byPhase("finishing", "last");
      // RTS = finishing end + 1 (usually the shipping chip date)
      const rtsDate = shippingChipDate || finishingLastDate || "";

      return {
        // Keep raw fields so editable columns still patch back correctly
        ...panel,

        // Derived identity.
        // projectName is load-bearing, not decoration: Premier staff say the job
        // out loud by name ("Shalimar", "Charlemagne"), and on 2026-08-13 Josh
        // typed S-H-A-L-I into the MPL search and got nothing back. Panels carry
        // only projectId — the name lives on the joined project and was never
        // copied onto the row, so it was neither searchable nor displayable.
        projectName: project?.projectName ?? "",
        projectNumber: project?.projectNumber ?? "",
        moldInstanceCode: moldInstance?.moldInstanceCode ?? "",

        // Concrete volume conversions
        concreteCY:
          volumeCuft != null ? Number((volumeCuft / 27).toFixed(2)) : "",
        concreteCF: volumeCuft ?? "",
        concreteSF: sqFt ?? "",

        // Scheduling dates derived from chips
        moldCompletionDate,
        setupDate,
        castDate,
        rtsDate,

        // Fall back to existing derived fields for productionArea / scheduledShipDate
        productionArea:
          schedule?.productionArea ?? panel.productionArea ?? "",
        scheduledShipDate:
          schedule?.scheduledShipDate ?? panel.scheduledShipDate ?? rtsDate ?? "",
        scheduledProductionDate:
          schedule?.scheduledProductionDate ??
          schedule?.scheduledDate ??
          panel.scheduledProductionDate ??
          castDate ??
          "",
        estimatedManHours:
          schedule?.estimatedManHours ?? panel.estimatedManHours ?? "",

        // Placeholders — DB columns don't exist yet. Rendered as empty
        // read-only cells in the grid; also shipped in the export for
        // round-trip consistency with the client's template.
        pieceDrawingLink: "",
        embedDrawingLink: "",
        bomList: "",
        pieceDrawingReleaseDate: "",
        materialDeliveryDate: "",
        repairIssued: "",
        repairDocumentLink: "",
        repairNotes: "",
        repairCompleted: "",
        repairPhotos: "",
        shippingStatus: "",
        shippingLoad: "",
        shippingDiagramLink: "",
        erectionSDLink: "",
        projectSDLink: "",
        erectionMaterialsBOM: "",
        fieldNotes: "",
        installStatus: "",
      };
    });

    this._entitiesCache = {
      panels: panelsRef,
      schedules: schedulesRef,
      chips: chipsRef,
      molds: moldsRef,
      projects: projectsRef,
      result,
    };
    return result;
  }

  // Track the count of unsaved edits (drives the Save button in the header).
  // Stored as a plain counter rather than the DataGrid namespace since the new
  // body patches live on every cell-edit commit.
  pendingEditCount = 0;

  // Legacy saveEdits stays around for the old DataGrid namespace path, but the
  // new body doesn't use it. Kept to avoid breaking any other screen that
  // might still call it.
  saveEdits = async () => {
    const dataGridStore = this.rootStore.dataGridStore as DataGridStore;
    const namespace = dataGridStore?.namespaces?.["MasterPieceListDataGrid"];
    if (!namespace?.editData) return;

    const panelsStore = this.rootStore.panelsStore as ServiceStore;
    if (!panelsStore) return;

    const entries = Object.entries(namespace.editData);
    for (const [id, changes] of entries) {
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(changes as Record<string, unknown>)) {
        if (MASTER_PIECE_LIST_EDITABLE_KEYS.has(k)) payload[k] = v;
      }
      if (Object.keys(payload).length > 0) {
        await panelsStore.patch(Number(id), payload);
      }
      namespace.removeEditData(id);
    }
  };

  /**
   * Patch a single field on a single panel. Used by the new DataGrid body
   * which persists on every cell-edit commit (no batching). Silently drops
   * writes to fields outside the editable allow-list.
   */
  patchPanelField = async (panelId: number, field: string, value: unknown): Promise<boolean> => {
    if (!MASTER_PIECE_LIST_EDITABLE_KEYS.has(field)) return false;
    const panelsStore = this.rootStore.panelsStore as ServiceStore;
    if (!panelsStore) return false;
    try {
      await panelsStore.patch(panelId, { [field]: value });
      runInAction(() => {
        this.pendingEditCount = Math.max(0, this.pendingEditCount - 1);
      });
      return true;
    } catch (error) {
      console.error("Failed to patch panel", panelId, field, error);
      return false;
    }
  };

  incrementPendingEdit = () => {
    runInAction(() => {
      this.pendingEditCount += 1;
    });
  };

  // Aggregate stats for the summary bar at the top of the page.
  get summary() {
    const list = this.entities;
    const totalVolume = list.reduce((sum, e) => {
      const v = (e as Record<string, unknown>).volumeCuft;
      return sum + (typeof v === "number" ? v : 0);
    }, 0);
    const totalWeight = list.reduce((sum, e) => {
      const w = (e as Record<string, unknown>).weightLbs;
      return sum + (typeof w === "number" ? w : 0);
    }, 0);
    const uniqueMolds = new Set(
      list
        .map((e) => (e as Record<string, unknown>).moldInstanceCode)
        .filter((m) => !!m)
    );
    const uniqueProjects = new Set(
      list
        .map((e) => (e as Record<string, unknown>).projectNumber)
        .filter((p) => !!p)
    );
    return {
      totalPieces: list.length,
      uniqueProjects: uniqueProjects.size,
      uniqueMolds: uniqueMolds.size,
      totalVolume: Math.round(totalVolume * 10) / 10,
      totalWeight: Math.round(totalWeight),
    };
  }

  async exportToExcel() {
    const entities = this.entities;
    if (!entities.length) return;

    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Master Piece List");

    const columns: Partial<import("exceljs").Column>[] = [
      { header: "Job", key: "projectName", width: 26 },
      { header: "Project #", key: "projectNumber", width: 15 },
      { header: "Type", key: "panelType", width: 18 },
      { header: "Mark Number", key: "panelMark", width: 18 },
      { header: "Control #", key: "uniquePieceIdentifier", width: 28 },
      { header: "Piece Drawing Link", key: "pieceDrawingLink", width: 28 },
      { header: "Embed Drawing Link", key: "embedDrawingLink", width: 28 },
      { header: "BOM List", key: "bomList", width: 28 },
      { header: "Priority", key: "priority", width: 10 },
      { header: "Mold", key: "moldInstanceCode", width: 14 },
      { header: "Location", key: "location", width: 18 },
      { header: "Volume", key: "volumeCuft", width: 12 },
      { header: "Weight", key: "weightLbs", width: 12 },
      { header: "Length", key: "lengthInches", width: 12 },
      { header: "Linear FT", key: "linearFt", width: 12 },
      { header: "Sq Ft", key: "sqFt", width: 12 },
      { header: "Concrete CY", key: "concreteCY", width: 12 },
      { header: "Concrete CF", key: "concreteCF", width: 12 },
      { header: "Concrete SF", key: "concreteSF", width: 12 },
      { header: "Level", key: "level", width: 10 },
      { header: "Color", key: "color", width: 16 },
      { header: "Finish", key: "finish", width: 16 },
      { header: "Mix Design", key: "mixDesign", width: 16 },
      { header: "Release PSI", key: "releaseStrength", width: 12 },
      { header: "Shipping PSI", key: "shippingStrength", width: 12 },
      { header: "Pour #", key: "pourNumber", width: 10 },
      { header: "PM/Drafting Notes", key: "pmDraftingNotes", width: 28 },
      { header: "Piece Drawing Release Date", key: "pieceDrawingReleaseDate", width: 18 },
      { header: "Material Delivery Date", key: "materialDeliveryDate", width: 18 },
      { header: "Mold Completion", key: "moldCompletionDate", width: 16 },
      { header: "Setup Date", key: "setupDate", width: 14 },
      { header: "Cast Date", key: "castDate", width: 14 },
      { header: "RTS (Ready to Ship) Date", key: "rtsDate", width: 18 },
      { header: "Production area/location", key: "productionArea", width: 18 },
      { header: "QC Status", key: "qcStatus", width: 14 },
      { header: "Post Pour Notes", key: "pstpNotes", width: 22 },
      { header: "Finishing Notes", key: "fcNotes", width: 22 },
      { header: "Repair Issued", key: "repairIssued", width: 14 },
      { header: "Repair Document Link", key: "repairDocumentLink", width: 22 },
      { header: "Repair Notes", key: "repairNotes", width: 22 },
      { header: "Repair Completed", key: "repairCompleted", width: 14 },
      { header: "Repair Photos", key: "repairPhotos", width: 22 },
      { header: "Shipping Status", key: "shippingStatus", width: 14 },
      { header: "Scheduled Shipping Date", key: "scheduledShipDate", width: 18 },
      { header: "Shipping Load #", key: "shippingLoad", width: 14 },
      { header: "Shipping Diagram Link", key: "shippingDiagramLink", width: 22 },
      { header: "Erection SD Link", key: "erectionSDLink", width: 22 },
      { header: "Project SD Link", key: "projectSDLink", width: 22 },
      { header: "Erection Materials BOM", key: "erectionMaterialsBOM", width: 22 },
      { header: "Field Notes", key: "fieldNotes", width: 22 },
      { header: "Install Status", key: "installStatus", width: 14 },
    ];

    sheet.columns = columns;

    // Style header row
    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF333333" },
    };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };
    headerRow.height = 22;

    // Add data rows
    for (const entity of entities) {
      const e = entity as Record<string, unknown>;
      const row: Record<string, unknown> = {};
      for (const col of columns) {
        if (col.key) row[col.key as string] = e[col.key as string] ?? "";
      }
      sheet.addRow(row);
    }

    // Add thin borders to all cells
    const thinBorder: import("exceljs").Border = {
      style: "thin",
      color: { argb: "FFD0D0D0" },
    };
    const totalRows = entities.length + 1;
    const totalCols = columns.length;
    for (let r = 1; r <= totalRows; r++) {
      const row = sheet.getRow(r);
      for (let c = 1; c <= totalCols; c++) {
        row.getCell(c).border = {
          top: thinBorder,
          left: thinBorder,
          bottom: thinBorder,
          right: thinBorder,
        };
      }
    }

    // Summary row
    const summaryRowNum = entities.length + 3;
    sheet.getRow(summaryRowNum).values = [
      `Total Pieces: ${entities.length}`,
    ];
    sheet.getRow(summaryRowNum).font = { bold: true };

    // Freeze header row
    sheet.views = [
      { state: "frozen", ySplit: 1, topLeftCell: "A2", activeCell: "A2" },
    ];

    // Write and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const today = new Date();
    const fileName = `Master_Piece_List_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}.xlsx`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export default MasterPieceListStore;
